import {Template} from "../../../../components/template/template.jsx";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import React, {useCallback, useMemo, useRef, useState} from "react";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaPlus, FaSearch} from "react-icons/fa";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import {useQuery} from "@tanstack/react-query";
import HolidayService from "../../../../services/web/holiday-service.js";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import {useFormNavigation} from "../../../../hooks/use-form-navigation.js";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../../utils/ag-grid-columns-utils.js";
import {monthsArrIdx1} from "./utils/holiday-utils.js";

const initialFilter = {
    name: ""
}

const pathModel = {
    create: {
        dev: `/holiday/form`,
        isBlank: false
    },
    edit: {
        dev: `/holiday/form`,
        isBlank: true
    }
}

const months = monthsArrIdx1;
const HolidayList = () => {
    const [filter, setFilter] = useState(initialFilter);
    const columnDefs = useMemo(() => [
        { field: 'id', headerName: "ID", hide: true},
        { field: 'day', headerName: "Dia", flex: 1},
        { field: 'month', headerName: "Mês", flex: 1, valueFormatter: ({value}) => months.find(month => String(month.value) === String(value)).label},
        { field: 'name', headerName: "Nome", flex: 10, filter: 'agTextColumnFilter',
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }},
    ], []);
    const [gridApi, setGridApi] = useState(null);
    const gridRef = useRef(null);
    const { onRowClicked, openForm } = useFormNavigation(pathModel);

    const defaultColDef = DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL

    const { data = [] } = useQuery({
        queryFn: HolidayService.list,
        queryKey: ["holiday"]
    })

    const handleInput = (target) => {
        setFilter(prevState => ({...prevState, [target.id]: target.value}));
    }

    const search = useCallback(() => {
        if (!gridApi) return;

        if(!filter.name){
            gridApi.setFilterModel(null);
            gridApi.onFilterChanged();
            return;
        }

        const filterModel = {};

        filterModel['name'] = {
            filterType: "text",
            type: "contains",
            filter: filter.name
        };

        gridApi.setFilterModel(filterModel);
        gridApi.onFilterChanged();
    }, [filter, gridApi]);



    const clearFilter = useCallback(() => {
        setFilter(initialFilter);
        const api = gridRef.current?.api;
        if (api) {
            api.setFilterModel(null);
            api.onFilterChanged();
        }
    }, []);

    useGlobalKey("Enter", search);
    useGlobalKey("Escape", clearFilter);

    return(
        <Template isLoading={false}>
            <Template.Header>
                <Template.Title>
                    Feriados
                </Template.Title>
                <Button onClick={openForm}><FaPlus/> Adicionar feriado</Button>
            </Template.Header>
            <Template.Body>
                <Card>
                    <Card.Body className="flex gap-4">
                        <div className="flex-1">
                            <InputForm id="name" label="Nome" value={filter.name} onChange={({target}) => handleInput(target)} />
                        </div>
                        <div className="flex gap-2">
                            <Button target="primary-outline" onClick={clearFilter} title="Limpar"><FaEraser/></Button>
                            <Button onClick={search} title="Buscar"><FaSearch/></Button>
                        </div>
                    </Card.Body>
                </Card>
                <Card className="mt-2">
                    <Card.Body>
                        <div className="h-[calc(100vh-153px)] aggrid-row-pointer">
                            <AgGridReact
                                onGridReady={(params) => {
                                    setGridApi(params.api);
                                }}
                                ref={gridRef}
                                rowData={data}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                pagination
                                onRowClicked={onRowClicked}
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme()}
                                onFilterChanged={aggridUtils.filterChangedNoRowsOverlay}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
}

export default HolidayList;